import { useState, useEffect, useMemo } from 'react';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import Process from './components/Process';
import Testimonials from './components/Testimonials';
import Blog from './components/Blog';
import Contact from './components/Contact';
import Footer from './components/Footer';
import SEOHead from '../../components/feature/SEOHead';

const siteUrl = (import.meta.env.VITE_SITE_URL as string) || '';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  const homeSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'RoofingContractor'],
    name: 'Good Pitch Roofing',
    description:
      'Good Pitch Roofing provides expert roof repair and replacement services in Oklahoma. We work directly with insurance companies to handle storm damage claims. Licensed roofing contractor serving all of Oklahoma.',
    url: siteUrl || 'https://example.com',
    telephone: '+14053972783',
    priceRange: '$$',
    image: `${siteUrl}/og-image.jpg`,
    logo: 'https://static.readdy.ai/image/efcca1ae64e2adea8f6cbcf3fc677bc7/dcb07f32778ecb4afada9f63cf477c36.png',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Oklahoma City',
      addressRegion: 'OK',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '35.4676',
      longitude: '-97.5164',
    },
    areaServed: {
      '@type': 'State',
      name: 'Oklahoma',
    },
    hasCredential: {
      '@type': 'EducationalOccupationalCredential',
      name: 'Oklahoma Roofing License',
      credentialCategory: 'license',
      identifier: '80007301',
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '07:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '08:00',
        closes: '14:00',
      },
    ],
    sameAs: [],
  }), []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Good Pitch Roofing | Oklahoma Insurance Roof Repair & Replacement Specialists"
        description="Good Pitch Roofing works directly with insurance companies to repair or replace storm-damaged roofs across Oklahoma. Free inspections. Licensed #80007301. Call 405-397-2783 today."
        canonical="/"
        keywords="Oklahoma roofing, insurance claim roofing, storm damage roof repair, roof replacement Oklahoma, Good Pitch Roofing"
        ogTitle="Good Pitch Roofing | Oklahoma's Trusted Insurance Roofing Experts"
        ogDescription="Expert roof repair and replacement. We work directly with your insurance company. Licensed #80007301. Call 405-397-2783 for a free inspection."
        ogType="website"
        schema={homeSchema}
      />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/90 backdrop-blur-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img
              src="https://static.readdy.ai/image/efcca1ae64e2adea8f6cbcf3fc677bc7/dcb07f32778ecb4afada9f63cf477c36.png"
              alt="Good Pitch Roofing Logo"
              className="h-12 w-auto"
            />
          </div>

          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-[#FF6B35] transition-colors text-sm font-medium cursor-pointer">About</button>
            <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-[#FF6B35] transition-colors text-sm font-medium cursor-pointer">Services</button>
            <button onClick={() => scrollToSection('process')} className="text-gray-700 hover:text-[#FF6B35] transition-colors text-sm font-medium cursor-pointer">Process</button>
            <button onClick={() => scrollToSection('testimonials')} className="text-gray-700 hover:text-[#FF6B35] transition-colors text-sm font-medium cursor-pointer">Testimonials</button>
            <button onClick={() => scrollToSection('blog')} className="text-gray-700 hover:text-[#FF6B35] transition-colors text-sm font-medium cursor-pointer">Blog</button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-[#FF6B35] transition-colors text-sm font-medium cursor-pointer">Contact</button>
          </div>

          <button
            onClick={() => scrollToSection('contact')}
            className="bg-[#FF6B35] text-white px-6 py-3 rounded-full hover:bg-[#ff5722] transition-colors text-sm font-semibold whitespace-nowrap cursor-pointer"
          >
            Free Inspection
          </button>
        </div>
      </nav>

      <Hero scrollToContact={() => scrollToSection('contact')} />
      <About />
      <Services />
      <Process scrollToContact={() => scrollToSection('contact')} />
      <Testimonials />
      <Blog />
      <Contact />
      <Footer scrollToSection={scrollToSection} />
    </div>
  );
}